import { SchemaIconFont } from '@infotech-soe/merps/types/Metadata';
import {
  ActionName,
  CategoryActionP,
  CategoryActionSchema,
  CategoryConditionP,
  CategoryName,
  CategorySchema,
  ConditionSetP,
  DisplayModeName,
  DomainName,
  DomainsDefinition,
  DomainsSchema,
  ExecutedActionP,
  IApplicationSchema,
  ICategoryFieldSanitized,
  ICategoryParsed,
  IContextMenuParsed,
  IContextMenuSchema,
  IDisplayModeParsed,
  IDisplayModeSchema,
  IFormParsed,
  IFormSchema,
  IMainMenuParsed,
  IMainMenuSchema,
  IViewParsed,
  IViewSchema,
  TFormName,
  TViewName,
} from '@infotech-soe/metaschema';
import { fromPairs } from 'lodash';

import { ConcurrentLRUCache } from '../../../framework/caching/ConcurrentLRUCache';
import {
  getLogger,
  InjectDebugLogger,
} from '../../../framework/logging/decorators/InjectDebugLogger';
import { LogFormat } from '../../../framework/logging/decorators/LogFormat';
import { LogMethod } from '../../../framework/logging/decorators/LogMethod';
import IndexedDBObjectStorage from '../../persistence/IndexedDBObjectStorage';
import ObjectStorage from '../../persistence/ObjectStorage';
import IApiProvider from '../../providers/abstract/IApiProvider';
import MerpsApiProvider from '../../providers/pb-adapter/MerpsApiProvider';
import ISchemaParser from '../parser/ISchemaParser';
import MetaschemaParser from '../parser/MetaschemaParser';

import DefaultStoredSchemaNameManager from './DefaultStoredSchemaNameManager';
import { FieldDomainResolver } from './FieldDomainResolver';
import ISchemaRepository, { IViewHydrated } from './ISchemaRepository';
import IStoredSchemaNameManager, {
  StoredSchemaName,
} from './IStoredSchemaNameManager';
import { NamedDomain } from './NamedDomain';
import { IFieldSchema } from './storage';
import {
  StoredActionArgsDomain,
  StoredCategoryActions,
  StoredCategoryList,
  StoredCategorySource,
  StoredDomainList,
  StoredFormFields,
  StoredFormSource,
  StoredSchemaSource,
  StoredViewSource,
} from './StoredSchemaSource';

/**
 * Maximum number of entries in {@link AsyncLRUCache} of schemas.
 */
const MAX_CACHE_SIZE = 10000;

/**
 * Name of {@link ObjectStorage} store for {@link StoredSchemaSource} objects.
 */
export const STORAGE_NAME = 'schemas';

type DomainsSchemasList = string[];

type AnySchema =
  | IApplicationSchema
  | DomainsDefinition
  | ICategoryParsed
  | IViewParsed
  | IFormParsed
  | CategoryActionP
  | IDisplayModeParsed
  | IMainMenuParsed
  | ActionName[]
  | IContextMenuParsed
  | DomainsSchemasList
  | ICategoryFieldSanitized
  | ConditionSetP
  | SchemaIconFont;

/**
 * Default implementation of {@link ISchemaRepository}.
 *
 * This schema repository uses {@link IApiProvider} to fetch schema sources
 * over network, caches them on disk using {@link ObjectStorage} and caches
 * up to {@link MAX_CACHE_SIZE} most recently used parsed schema definitions
 * in memory using {@link AsyncLRUCache}.
 */
@InjectDebugLogger('schemas')
class CachedSchemaRepositorySingleton implements ISchemaRepository {
  private readonly cache: ConcurrentLRUCache<StoredSchemaName, AnySchema>;
  private readonly domainResolver: FieldDomainResolver;
  private readonly api: IApiProvider;
  private readonly storage: ObjectStorage<
    | StoredSchemaSource
    | StoredDomainList
    | StoredCategoryList
    | StoredViewSource
    | StoredFormSource
  >;
  private readonly parser: ISchemaParser;
  private readonly nameManager: IStoredSchemaNameManager;
  constructor() {
    this.api = new MerpsApiProvider();
    this.storage = new IndexedDBObjectStorage();
    this.cache = new ConcurrentLRUCache(MAX_CACHE_SIZE, this.loadSchema);
    this.parser = new MetaschemaParser();
    this.nameManager = new DefaultStoredSchemaNameManager();
    this.storage.open(STORAGE_NAME);
    this.domainResolver = new FieldDomainResolver(
      this.parser,
      this.api,
      this.storage,
      this.nameManager
    );
  }

  public async getCategoryActionDomains(
    category: CategoryName,
    action: ActionName
  ): Promise<StoredActionArgsDomain> {
    const actions: StoredCategoryActions = (await this.cache.getEntry(
      this.nameManager.buildCategoryActionsName(category)
    )) as any;

    return actions[action];
  }

  @LogMethod
  public getAction(
    @LogFormat('%s') category: CategoryName,
    @LogFormat('%s') action: ActionName
  ): Promise<CategoryActionP> {
    return this.cache.getEntry(
      this.nameManager.buildActionName(category, action)
    ) as Promise<CategoryActionP>;
  }

  @LogMethod
  public async getListCategoryActions(
    @LogFormat('%s') category: CategoryName
  ): Promise<ActionName[]> {
    const actions: StoredCategoryActions = (await this.cache.getEntry(
      this.nameManager.buildCategoryActionsName(category)
    )) as any;

    return Object.keys(actions);
  }

  @LogMethod
  public getCategory(
    @LogFormat('%s') name: CategoryName
  ): Promise<ICategoryParsed> {
    return this.cache.getEntry(
      this.nameManager.buildCategoryName(name)
    ) as Promise<ICategoryParsed>;
  }

  @LogMethod
  public getDisplayMode(
    @LogFormat('%s') category: CategoryName,
    @LogFormat('%s') displayMode: DisplayModeName
  ): Promise<IDisplayModeParsed> {
    return this.cache.getEntry(
      this.nameManager.buildDisplayModeName(category, displayMode)
    ) as Promise<IDisplayModeParsed>;
  }

  @LogMethod
  public async getDomain(
    @LogFormat('%s') name: DomainName
  ): Promise<NamedDomain> {
    const domains = (await this.cache.getEntry(
      this.nameManager.buildDomainsName('all')
    )) as StoredDomainList;

    const domain = domains[name];

    if (domain) {
      return domain;
    }

    throw new Error('Domain not found: ' + name);
  }

  @LogMethod
  public getForm(
    @LogFormat('%s') category: CategoryName,
    @LogFormat('%s') form: TFormName
  ): Promise<IFormParsed> {
    return this.cache.getEntry(
      this.nameManager.buildFormName(category, form)
    ) as Promise<IFormParsed>;
  }

  public async getFormFieldsDomain(
    category: CategoryName,
    form: TFormName
  ): Promise<StoredFormFields> {
    const stored: StoredFormSource = (await this.storage.get(
      this.nameManager.buildFormName(category, form)
    )) as any;

    return stored.fields || {};
  }

  @LogMethod
  public getApplication(
    @LogFormat('%s') application: string
  ): Promise<IApplicationSchema> {
    return this.cache.getEntry(
      this.nameManager.buildApplicationName(application)
    ) as Promise<IApplicationSchema>;
  }

  @LogMethod
  public getMenu(
    @LogFormat('%s') application: string
  ): Promise<IMainMenuParsed> {
    return this.cache.getEntry(
      this.nameManager.buildMenuName(application)
    ) as Promise<IMainMenuParsed>;
  }

  @LogMethod
  public async getView(
    @LogFormat('%s') name: TViewName
  ): Promise<IViewHydrated> {
    const parsed: IViewParsed = (await this.cache.getEntry(
      this.nameManager.buildViewName(name)
    )) as any;

    const stored: StoredViewSource = (await this.storage.get(
      this.nameManager.buildViewName(name)
    )) as any;

    for (const [fieldName, def] of Object.entries(parsed.Fields)) {
      parsed.Fields[fieldName] = {
        ...def,
        domain: stored?.fields[fieldName],
      };
    }

    return parsed as IViewHydrated;
  }

  @LogMethod
  public async getTemplate(
    kind: string,
    name: string
  ): Promise<IContextMenuParsed> {
    return this.cache.getEntry(
      this.nameManager.buildTemplateName(kind, name)
    ) as Promise<IContextMenuParsed>;
  }

  @LogMethod
  public async getConditionsSet(
    category: CategoryName,
    name: string
  ): Promise<CategoryConditionP> {
    return this.cache.getEntry(
      this.nameManager.buildConditionsSetName(category, name)
    ) as Promise<CategoryConditionP>;
  }

  @LogMethod
  public async getField(
    category: CategoryName,
    path: string
  ): Promise<ICategoryFieldSanitized> {
    return this.cache.getEntry(
      this.nameManager.buildFieldName(category, path)
    ) as Promise<ICategoryFieldSanitized>;
  }

  @LogMethod
  public async getCategoryExtendedProperties(
    @LogFormat('%s') category: CategoryName
  ): Promise<{ linkedFiles?: boolean; actionHistory?: boolean }> {
    const stored = (await this.storage.get(
      this.nameManager.buildCategoryName(category)
    )) as StoredCategorySource | undefined;

    return {
      linkedFiles: stored?.linkedFiles,
      actionHistory: stored?.actionHistory,
    };
  }

  @LogMethod
  public getIconFont(): Promise<SchemaIconFont> {
    return this.cache.getEntry('iconFont') as Promise<SchemaIconFont>;
  }

  @LogMethod
  public async purgeCache(): Promise<void> {
    this.cache.clearEntries();
    await this.storage.deleteAll();
  }

  /**
   * Loads and parses schema to save it in LRU cache. This method is used as
   * a factory for {@link CachedSchemaRepository.cache}.
   *
   * @param name - Cache key (schema name).
   * @param isSecondLoad
   */
  private readonly loadSchema = async (
    name: StoredSchemaName,
    isSecondLoad = false
  ): Promise<AnySchema> => {
    const log = getLogger(CachedSchemaRepositorySingleton);

    log('schema %s not found in RAM cache, looking up in disk cache', name);

    let source;
    let data;
    let schema;
    let fetchName = name;
    const [type, categoryName, key] = name.split('/');

    switch (type) {
      case 'domains':
        data = (await this.storage.get(
          this.nameManager.buildResolvedDomainsList()
        )) as DomainsDefinition;
        break;
      case 'actions': {
        const categoryKey = this.nameManager.buildCategoryName(categoryName);
        const categoryEntry = await this.storage.get(categoryKey);
        source = categoryEntry?.source;
        if (!source) {
          const actionEntry = await this.storage.get(name);
          source = actionEntry?.source;
          if (!source) {
            fetchName = categoryKey;
          }
        }
        break;
      }
      case 'categoryActions': {
        const found = await this.storage.get(
          this.nameManager.buildCategoryName(categoryName)
        );
        source = (found as StoredCategorySource)?.categoryActions;
        break;
      }
      case 'fields': {
        const found = await this.storage.get(
          this.nameManager.buildCategoryName(categoryName)
        );
        source = (found as StoredCategorySource)?.fields?.[key];
        break;
      }
      case 'forms': {
        const found = await this.storage.get(
          this.nameManager.buildFormName(categoryName, key)
        );
        source = found?.source;
        break;
      }
      case 'iconFont': {
        data = await this.api.schemas.getIconFont();
        break;
      }
      default:
        schema = await this.storage.get(name);
        source = schema?.source;
    }

    if (data) return data;

    if (source) {
      return this.parseStoredSchema(name, source);
    }

    log('schema %s not found in disk cache, trying to fetch from server', name);

    if (!isSecondLoad) {
      await this.fetchSchema(fetchName);
      return this.loadSchema(name, true);
    }

    if (!source) {
      throw new Error('Schema not found: ' + name);
    }

    return this.parseStoredSchema(name, source);
  };

  /**
   * Parses a schema source loaded from disk cache
   * (i.e., {@link ObjectStorage}).
   *
   * @param name - Schema name (i.e., cache key).
   * @param source - Cache entry object.
   */
  private async parseStoredSchema(
    name: StoredSchemaName,
    source: any
  ): Promise<AnySchema> {
    return this.nameManager.parseSchemaName<Promise<AnySchema>>(name, {
      onDomains: () =>
        this.storage.get(
          this.nameManager.buildResolvedDomainsList()
        ) as Promise<DomainsDefinition>,
      onDomainsList: async () => source,
      onCategory: async category =>
        this.parser.parseCategory(category, source).category,
      onCategoryActions: async () => source,
      onConditionsSet: async () => this.parser.parseConditionsSet(name, source),
      onView: async view => this.parser.parseView(view, source),
      onForm: async (category, form) =>
        this.parser.parseForm(category, form, source),
      onAction: async (category, action) =>
        this.parser.parseAction(category, action, source),
      onDisplayMode: async (category, displayMode) =>
        this.parser.parseDisplayMode(category, displayMode, source),
      onApplication: async application =>
        this.parser.parseApplication(application, source),
      onMenu: async application => this.parser.parseMenu(application, source),
      onTemplate: async (_kind, template) =>
        this.parser.parseTemplate(template, source),
      onFields: async () => source as ICategoryFieldSanitized,
    });
  }

  /**
   * Requests a schema from server and stores all the schemas the server
   * responded with in disk cache.
   *
   * @param name - Schema name (i.e., our cache key, not the original file
   * name on server).
   */
  private async fetchSchema(name: StoredSchemaName): Promise<void> {
    const api = this.api.schemas;

    const schemas = await this.nameManager.parseSchemaName(name, {
      onDomains: () => api.getDomainsSchema(),
      onDomainsList: () => api.getDomainsSchema(),
      onCategory: category => api.getCategorySchema(category),
      onCategoryActions: category => api.getCategorySchema(category),
      onConditionsSet: () => api.getConditionsSetSchema(name),
      onView: view => api.getViewSchema(view),
      onForm: (category, form) => api.getFormSchema(category, form),
      onAction: (category, action) => api.getActionSchema(category, action),
      onDisplayMode: (category, displayMode) =>
        api.getDisplayModeSchema(category, displayMode),
      onApplication: application => api.getApplicationSchema(application),
      onMenu: application => api.getMenuSchema(application),
      onTemplate: (kind, template) => api.getTemplate(kind, template),
      onFields: async (category: string, field: string) =>
        [
          {
            type: 'fields',
            category,
            field,
            schema: {
              source: await api.getField(category, field),
            },
          },
        ] as any,
    });

    for (const schema of schemas) {
      switch (schema.type) {
        case 'domains':
          await this.storeDomainsSchema(schema);
          break;

        case 'category':
          await this.storeCategorySchema(schema);
          break;

        case 'view':
          await this.storeViewSchema(schema);
          break;

        case 'form':
          await this.storeFormSchema(schema);
          break;

        case 'action':
          await this.storeActionSchema(schema);
          break;

        case 'displayMode':
          await this.storeDisplayModeSchema(schema);
          break;

        case 'mainMenu':
          await this.storeMenuSchema(schema);
          break;

        case 'application':
          await this.storeApplicationSchema(schema);
          break;
        case 'contextMenu':
          await this.storeTemplateSchema(schema);
          break;
        case 'fields':
          await this.storeFieldDomain(schema);
          break;
      }
    }
  }

  /**
   * Saves `schema` in {@link CachedSchemaRepository.cache} as `name`.
   *
   * @param name - Schema name (cache key).
   * @param source - Schema source object as returned by server.
   */
  private storeSchema<S = StoredSchemaSource>(
    name: StoredSchemaName,
    source: S
  ): Promise<void> {
    return this.storage.set(name, source as any);
  }

  private async extendDomainsCache(
    name: string,
    source: string
  ): Promise<void> {
    const namedDomains = Object.entries(
      this.parser.parseDomains(name, source)
    ).reduce<Record<string, NamedDomain>>((acc, [k, v]) => {
      acc[k] = new NamedDomain(k, v);
      return acc;
    }, {});
    await this.storage.set(this.nameManager.buildResolvedDomainsList(), {
      ...(await this.storage.get(this.nameManager.buildResolvedDomainsList())),
      ...namedDomains,
    });
  }

  /**
   * Saves domains `schema` in {@link CachedSchemaRepository.cache}.
   *
   * @param schema - Domains schema source object as returned by server.
   */
  private async storeDomainsSchema({
    name,
    source,
  }: DomainsSchema): Promise<void> {
    await this.extendDomainsCache(name, source);

    const currentList: any = await this.storage.get(
      this.nameManager.buildDomainSourcesName()
    );

    const schemaNames = (currentList as StoredCategoryList)?.source || [];

    if (!schemaNames.includes(name)) {
      schemaNames.push(name);
    }

    await this.storage.set(this.nameManager.buildDomainSourcesName(), {
      source: schemaNames,
    });
  }

  /**
   * Saves category `input` and derived action schemas in
   * {@link CachedSchemaRepository.cache}.
   *
   * @param input - Category input source object as returned by server.
   */
  private async storeCategorySchema(
    input: CategorySchema & {
      linkedFiles?: boolean;
      actionHistory?: boolean;
    }
  ): Promise<void> {
    const { name, source, linkedFiles, actionHistory } = input;
    const parseResult = this.parser.parseCategory(name, source);
    const resolvedActions = fromPairs(
      await Promise.all(
        parseResult.actions.map(async ({ name: actionName }) => {
          const action = parseResult.actions.find(
            ({ name: currentActionName }) => actionName === currentActionName
          );

          const argsDomains = await this.domainResolver.resolveActionInArgs(
            action?.schema as ExecutedActionP,
            parseResult.category,
            name
          );

          return [actionName, argsDomains];
        })
      )
    );

    await this.storeSchema<StoredCategorySource>(
      this.nameManager.buildCategoryName(name),
      {
        categoryActions: resolvedActions,
        fields: {},
        linkedFiles,
        actionHistory,
        source,
      }
    );
  }

  /**
   * Saves view `schema` in {@link CachedSchemaRepository.cache}.
   *
   * @param view.schema - View schema source object as returned by server.
   */
  private async storeViewSchema(view: IViewSchema): Promise<void> {
    const { name, source } = view;

    return this.storeSchema<StoredViewSource>(
      this.nameManager.buildViewName(name),
      {
        source,
        fields: await this.domainResolver.resolveView(view),
      }
    );
  }

  /**
   * Saves form `schema` in {@link CachedSchemaRepository.cache}.
   *
   * @param schema - Form schema source object as returned by server.
   */
  private async storeFormSchema({
    category,
    name,
    source,
  }: IFormSchema): Promise<void> {
    return this.storeSchema<StoredFormSource>(
      this.nameManager.buildFormName(category, name),
      {
        source,
        fields: await this.domainResolver.resolveFormFields(
          source,
          name,
          category
        ),
      }
    );
  }

  /**
   * Saves action `schema` in {@link CachedSchemaRepository.cache}.
   *
   * @param schema - Action schema source object as returned by server.
   */
  private storeActionSchema({
    category,
    name,
    source,
  }: CategoryActionSchema): Promise<void> {
    return this.storeSchema(this.nameManager.buildActionName(category, name), {
      source,
    });
  }

  /**
   * Saves display mode `schema` in {@link CachedSchemaRepository.cache}.
   *
   * @param schema - Display mode source object as returned by server.
   */
  private storeDisplayModeSchema({
    category,
    name,
    source,
  }: IDisplayModeSchema): Promise<void> {
    return this.storeSchema(
      this.nameManager.buildDisplayModeName(category, name),
      { source }
    );
  }

  /**
   * Saves menu `schema` in {@link CachedSchemaRepository.cache}.
   *
   * @param schema - Menu schema object as returned by server.
   */
  private storeMenuSchema({
    name,
    source,
  }: Omit<IMainMenuSchema, 'definition'>): Promise<void> {
    return this.storeSchema(this.nameManager.buildMenuName(name), {
      source,
    });
  }

  /**
   * Saves application `schema` in {@link CachedSchemaRepository.cache}.
   *
   * @param schema - Application schema object as returned by server.
   */
  private storeApplicationSchema({
    name,
    source,
  }: Omit<IApplicationSchema, 'definition'>): Promise<void> {
    return this.storeSchema(this.nameManager.buildApplicationName(name), {
      source,
    });
  }

  /**
   * Saves template `schema` in {@link CachedSchemaRepository.cache}.
   *
   * @param schema - Application schema object as returned by server.
   */
  private storeTemplateSchema({
    name,
    source,
  }: IContextMenuSchema): Promise<void> {
    return this.storeSchema(this.nameManager.buildTemplateName('menus', name), {
      source,
    });
  }

  private async storeFieldDomain({
    category,
    field,
    schema: { source },
  }: IFieldSchema): Promise<void> {
    const categoryName = this.nameManager.buildCategoryName(category);
    const categorySource: StoredCategorySource | undefined =
      (await this.storage.get(categoryName)) as any;

    await this.storeSchema(categoryName, {
      ...(categorySource as StoredSchemaSource),
      fields: {
        ...(categorySource?.fields as any),
        [field]: source,
      },
    });
  }
}

const CachedSchemaRepository = (() => {
  let instance: ISchemaRepository;

  return new Proxy(CachedSchemaRepositorySingleton, {
    construct(
      Target: typeof CachedSchemaRepositorySingleton
    ): ISchemaRepository {
      if (!instance) {
        instance = new Target();
      }
      return instance;
    },
  });
})();

export default CachedSchemaRepository;
